'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface ForgotFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotFormData>();

  const onSubmit = async (data: ForgotFormData) => {
    setLoading(true);
    try {
      await resetPassword(data.email);
    } catch {
      // Intentionally silent — don't reveal if email exists
    } finally {
      setLoading(false);
      // Always show success message (anti-enumeration)
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl text-primary tracking-tight">PrestaMass</span>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 card-shadow">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-success" />
              </div>
              <h1 className="text-xl font-800 text-foreground">Revisa tu correo</h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Si el correo está registrado en PrestaMass, recibirás instrucciones para recuperar tu contraseña en los próximos minutos.
              </p>
              <p className="text-xs text-muted-foreground">
                Revisa también tu carpeta de spam o correo no deseado.
              </p>
              <Link
                href="/sign-up-login"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 bg-primary text-white text-sm font-600 rounded-xl hover:bg-primary/90 transition-all"
              >
                <ArrowLeft size={14} />
                Volver al inicio de sesión
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-800 text-foreground">Recuperar contraseña</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Ingresa tu correo y te enviaremos instrucciones para restablecer tu contraseña.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    <Mail size={13} className="inline mr-1.5" />
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    {...register('email', {
                      required: 'El correo es obligatorio',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Correo inválido' },
                    })}
                    className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${
                      errors.email ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'
                    }`}
                  />
                  {errors.email && <p className="text-danger text-xs mt-1.5">{errors.email.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-600 rounded-xl hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-60 text-sm"
                >
                  {loading ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando...</>
                  ) : (
                    <><Send size={15} />Recuperar contraseña</>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/sign-up-login"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft size={14} />
                  Volver al inicio de sesión
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
